import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { UserContext } from '../components/UserProvider';
import Error from '../components/Error';
import Loader from '../components/Loader';
import { Router } from '../routes.mjs';
import { saveRouterState } from './routerState';
import { hasGlobalRole, hasRoleInAnyGroup } from './hasUserRoles';

const withRestrictedAccess = (Component) => {
  const WrappedComponent = ({
    requiredRoles,
    ...properties
  }) => {
    const {
      globalRolesAll,
      globalRolesAny,
      scopedRolesAll,
      scopedRolesAny,
    } = requiredRoles;

    const router = useRouter();
    const { user, loading, roles } = useContext(UserContext);

    useEffect(() => {
      if (!user && !loading) {
        saveRouterState(router);
        Router.replaceRoute('login');
      }
    }, [router, user, loading]);

    if (loading) return <Loader />;

    const userRoles = roles ?? {};

    if (
      (globalRolesAll && globalRolesAll.some(
        (role) => !hasGlobalRole(userRoles, role),
      ))
      || (globalRolesAny && globalRolesAny.every(
        (role) => !hasGlobalRole(userRoles, role),
      ))
      || (scopedRolesAll && scopedRolesAll.some(
        (role) => !hasRoleInAnyGroup(userRoles, role),
      ))
      || (scopedRolesAny && scopedRolesAny.every(
        (role) => !hasRoleInAnyGroup(userRoles, role),
      ))
    ) {
      return <Error error="access denied" />;
    }

    return (
      <Component {...properties} />
    );
  };

  WrappedComponent.propTypes = {
    requiredRoles: PropTypes.shape({
      globalRolesAll: PropTypes.arrayOf(
        PropTypes.string,
      ),
      globalRolesAny: PropTypes.arrayOf(
        PropTypes.string,
      ),
      scopedRolesAll: PropTypes.arrayOf(
        PropTypes.string,
      ),
      scopedRolesAny: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }).isRequired,
  };

  return WrappedComponent;
};

export default withRestrictedAccess;
